import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import { AuthProvider } from './context/AuthContext';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ModernLayout } from './components/layout/ModernLayout';
import './index.css';
import { ToastContainer } from 'react-toastify';
import 'react-image-crop/dist/ReactCrop.css';

// Route-level code splitting: each page becomes its own chunk so the public
// chat widget (and every other route) only downloads what it actually renders.
// Named-export pages are wrapped to expose a `default` for React.lazy.
const Login = lazy(() => import('./pages/Login').then((m) => ({ default: m.Login })));
const ModernDashboard = lazy(() => import('./pages/ModernDashboard').then((m) => ({ default: m.ModernDashboard })));
const Agents = lazy(() => import('./pages/Agents').then((m) => ({ default: m.Agents })));
const AgentConfig = lazy(() => import('./pages/AgentConfig'));
const Chatbots = lazy(() => import('./pages/Chatbots').then((m) => ({ default: m.Chatbots })));
const CreateChatbot = lazy(() => import('./pages/CreateChatbot').then((m) => ({ default: m.CreateChatbot })));
const Analytics = lazy(() => import('./pages/Analytics').then((m) => ({ default: m.Analytics })));
const ChatbotConfig = lazy(() => import('./pages/ChatbotConfig'));
const Workflows = lazy(() => import('./pages/Workflows').then((m) => ({ default: m.Workflows })));
const WorkflowEditor = lazy(() => import('./pages/WorkflowEditor'));
const Reports = lazy(() => import('./pages/Reports').then((m) => ({ default: m.Reports })));
const Settings = lazy(() => import('./pages/Settings').then((m) => ({ default: m.Settings })));
const NotFound = lazy(() => import('./pages/NotFound').then((m) => ({ default: m.NotFound })));
const AgentTypes = lazy(() => import('./pages/AgentTypes').then((m) => ({ default: m.AgentTypes })));
const McpOauthCallback = lazy(() => import('./pages/McpOauthCallback'));
const Signup = lazy(() => import('./pages/Signup'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const TenantListing = lazy(() => import('./pages/SuperAdmin/TenantListing'));
const AgentType = lazy(() => import('./pages/SuperAdmin/AgentType'));
const TenantConfig = lazy(() => import('./pages/SuperAdmin/TenantConfig'));
const McpCatalogPage = lazy(() => import('./pages/SuperAdmin/McpCatalog'));
const ReRouting = lazy(() => import('./pages/ReRouting').then((m) => ({ default: m.ReRouting })));
const Departments = lazy(() => import('./pages/Departments'));
const UsersList = lazy(() => import('./pages/UsersList'));
const PublicChatWidget = lazy(() => import('./pages/PublicChatWidget'));
const PublicEcosystemChatWidget = lazy(() => import('./pages/PublicEcosystemChatWidget'));
const Ecosystems = lazy(() => import('./pages/Ecosystems'));
const EcosystemConfig = lazy(() => import('./pages/EcosystemConfig'));

// Lightweight, dependency-free fallback shown while a route chunk loads.
const RouteFallback: React.FC = () => (
  <div className="flex min-h-screen items-center justify-center bg-agentic-white-off">
    <div
      className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-gray-500"
      role="status"
      aria-label="Loading"
    />
  </div>
);


/**
 * Main App component that sets up routing and global providers
 * 
 * @returns {JSX.Element} The main application component
 */
const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ToastContainer position="top-right" />
      <Router>
        <AuthProvider>
          <AppProvider>
            <div className="min-h-screen bg-agentic-white-off">
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Public Routes */}
                <Route 
                  path="/login" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <Login />
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/signup" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <Signup />
                    </ProtectedRoute>
                  } 
                />
                
                <Route 
                  path="/forgot-password" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <ForgotPassword />
                    </ProtectedRoute>
                  } 
                />

                <Route 
                  path="/reset-password/verify" 
                  element={
                    <ProtectedRoute requireAuth={false}>
                      <ResetPassword />
                    </ProtectedRoute>
                  } 
                />

                {/* Public Chat Widget Route - No Auth Required */}
                <Route
                  path="/chat/:chatbotId/:clientId"
                  element={<PublicChatWidget />}
                />

                {/* Public Ecosystem Chat Widget Route - No Auth Required */}
                <Route
                  path="/eco-chat/:ecosystemId/:clientId"
                  element={<PublicEcosystemChatWidget />}
                />

                <Route
                  path="/oauth/mcp/callback"
                  element={<McpOauthCallback />}
                />

                {/* Protected Routes */}
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <ModernLayout>
                        <Routes>
                          <Route path="/" element={<ReRouting />} />
                          <Route path="/dashboard" element={<ModernDashboard />} />
                          <Route path="/agents" element={<Agents />} />
                          <Route path="/agents/:agentId/config" element={<AgentConfig />} />
                          <Route path="/agent-types" element={<AgentTypes />} />
                          <Route path="/chatbots" element={<Chatbots />} />
                          <Route path="/chatbots/create" element={<CreateChatbot />} />
                          <Route path="/chatbots/:chatbotId/config" element={<ChatbotConfig />} />
                          <Route path="/analytics" element={<Analytics />} />
                          <Route path="/workflows" element={<Workflows />} />
                          <Route path="/workflows/new" element={<WorkflowEditor />} />
                          <Route path="/workflows/:workflowId/edit" element={<WorkflowEditor />} />
                          <Route path="/reports" element={<Reports />} />
                          {/* <Route path="/users" element={<Users />} /> */}
                          <Route path="/settings" element={<Settings />} />
                          <Route path="/tenant-listing" element={<TenantListing />} />
                          <Route path="/super-agent-type" element={<AgentType />} />
                          <Route path="/super-mcp-catalog" element={<McpCatalogPage />} />
                          <Route path="/tenant/:tenantId/config" element={<TenantConfig />} />
                          <Route path="/departments" element={<Departments />} />
                          <Route path="/users" element={<UsersList />} />
                          <Route path="/ecosystems" element={<Ecosystems />} />
                          <Route path="/ecosystems/:ecosystemId/config" element={<EcosystemConfig />} />
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </ModernLayout>
                    </ProtectedRoute>
                  }
                />
              </Routes>
              </Suspense>
            </div>
          </AppProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
